import { z } from 'zod';
import { ADDON_PRODUCTS } from './addons';
import { type ScriptOptions, scriptDraftOptionsSchema, scriptOptionsSchema } from './script';
import { TURNAROUND_DAYS, type TurnaroundDays } from './shared';
import { type TitleOptions, titleDraftOptionsSchema, titleOptionsSchema } from './title';

export * from './addons';
export * from './script';
export * from './shared';
export * from './title';

/** The products a new order can be for. */
export const PRODUCTS = ['title', 'script'] as const;
export const productSchema = z.enum(PRODUCTS);
export type Product = z.infer<typeof productSchema>;

/** Every product an order row can hold: what a new order is for and the add-on packs. */
export const STORED_PRODUCTS = [...PRODUCTS, ...ADDON_PRODUCTS] as const;
export const storedProductSchema = z.enum(STORED_PRODUCTS);
export type StoredProduct = z.infer<typeof storedProductSchema>;

/** The turnarounds each product offers, so one can be dropped for one product without a schema change. */
export const PRODUCT_TURNAROUNDS: Record<Product, readonly TurnaroundDays[]> = {
  title: TURNAROUND_DAYS,
  script: TURNAROUND_DAYS,
};

/** Options type per product. */
export type ProductOptions = { title: TitleOptions; script: ScriptOptions };

const OPTION_SCHEMAS = { title: titleOptionsSchema, script: scriptOptionsSchema } as const;

/** The options schema of a product. */
export function productOptionsSchema<P extends Product>(product: P): (typeof OPTION_SCHEMAS)[P] {
  return OPTION_SCHEMAS[product];
}

const DRAFT_OPTION_SCHEMAS = {
  title: titleDraftOptionsSchema,
  script: scriptDraftOptionsSchema,
} as const;

/** The options a DRAFT of a product may hold: the product's own, minus the choices a draft may leave open. */
export function productDraftOptionsSchema<P extends Product>(
  product: P,
): (typeof DRAFT_OPTION_SCHEMAS)[P] {
  return DRAFT_OPTION_SCHEMAS[product];
}
